/**
 * Visitor identity persistence — dual-layer to survive partial storage wipes.
 *
 * Strategy: write to BOTH localStorage AND a first-party cookie. Read tries
 * localStorage first (faster, no parsing), falls back to cookie. If both are
 * cleared by the user, the next handshake generates a fresh visitor_id and
 * starts a new conversation thread — which is the intended UX.
 */

import type { StoredTouch } from '../lib/attribution';

const STORAGE_KEY = 'mirlo_visitor_id';
const COOKIE_NAME = 'mirlo_visitor_id';
const COOKIE_TTL_DAYS = 365;
const WELCOME_DISMISSED_PREFIX = 'mirlo_wc_welcome_dismissed';
const ATTRIBUTION_KEY = 'mirlo_wc_attribution';

function safeLocalStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Quota exceeded, private mode, blocked by host — fall back to cookie only.
  }
}

function safeSessionStorageGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionStorageSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Unavailable: the bubble shows again on the next page. Annoying, not broken.
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

function writeCookie(name: string, value: string, ttlDays: number): void {
  const expires = new Date(Date.now() + ttlDays * 86400 * 1000).toUTCString();
  const isSecure = window.location.protocol === 'https:';
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `expires=${expires}`,
    'path=/',
    'SameSite=Lax',
    isSecure ? 'Secure' : '',
  ]
    .filter(Boolean)
    .join('; ');
}

/** Returns the visitor_id from storage, or null if none is persisted. */
export function readVisitorId(): string | null {
  return safeLocalStorageGet(STORAGE_KEY) ?? readCookie(COOKIE_NAME);
}

/**
 * Persists the visitor_id to both localStorage and a first-party cookie.
 * The cookie expires in 1 year by default.
 */
export function writeVisitorId(visitorId: string): void {
  safeLocalStorageSet(STORAGE_KEY, visitorId);
  writeCookie(COOKIE_NAME, visitorId, COOKIE_TTL_DAYS);
}

/**
 * Whether the visitor already dealt with the welcome bubble in this browsing
 * session — either dismissing it or opening the chat from it.
 *
 * sessionStorage on purpose: the bubble is a proactive greeting, so it must not
 * reappear on every page of a multi-page site (the bug this replaces), but a
 * visitor coming back tomorrow should be greeted again. Scoped per widget so two
 * widgets on one page don't silence each other.
 */
export function readWelcomeDismissed(widgetKey: string): boolean {
  return (
    safeSessionStorageGet(`${WELCOME_DISMISSED_PREFIX}:${widgetKey}`) === '1'
  );
}

export function writeWelcomeDismissed(widgetKey: string): void {
  safeSessionStorageSet(`${WELCOME_DISMISSED_PREFIX}:${widgetKey}`, '1');
}

/**
 * Traffic source remembered across the visit (see `captureAttribution`).
 * Anything unreadable counts as nothing stored.
 */
export function readStoredAttribution(): StoredTouch | null {
  const raw = safeLocalStorageGet(ATTRIBUTION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredTouch> | null;
    if (
      !parsed ||
      typeof parsed.saved_at !== 'string' ||
      !parsed.touch ||
      typeof parsed.touch !== 'object' ||
      typeof parsed.touch.channel !== 'string'
    )
      return null;
    return parsed as StoredTouch;
  } catch {
    return null;
  }
}

export function writeStoredAttribution(value: StoredTouch): void {
  safeLocalStorageSet(ATTRIBUTION_KEY, JSON.stringify(value));
}
